import { useEffect, useState, type FormEvent } from "react";
import { api, ApiError, type Settings } from "../lib/api";
import { formatBytes, parseBytes } from "../lib/format";
import { useToast } from "./Toast";

interface Props {
  settings: Settings | null;
  onSaved: (settings: Settings) => void;
}

/** The upload rules: where files land, how big they may be, how they're named. */
export function SettingsPanel({ settings, onSaved }: Props) {
  const { notify } = useToast();
  const [draft, setDraft] = useState<Settings | null>(settings);
  const [sizeText, setSizeText] = useState("");
  const [mimeText, setMimeText] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset the form whenever the stored settings change under it.
  useEffect(() => {
    if (!settings) return;
    setDraft(settings);
    setSizeText(formatBytes(settings.max_size_bytes));
    setMimeText(settings.allowed_mime.join(", "));
  }, [settings]);

  if (!draft) {
    return (
      <section className="panel">
        <header className="panel__head">
          <span className="label">Upload rules</span>
        </header>
        <div className="panel__body">
          <p className="field__hint">Loading</p>
        </div>
      </section>
    );
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (!draft) return;

    const maxSizeBytes = parseBytes(sizeText);
    if (maxSizeBytes === null || maxSizeBytes <= 0) {
      setError('Enter a size such as "32MB" or a plain number of bytes.');
      return;
    }

    setError(null);
    setIsSaving(true);
    try {
      const saved = await api.settings.update({
        base_uri: draft.base_uri.trim(),
        policy_id: draft.policy_id.trim(),
        max_size_bytes: maxSizeBytes,
        allowed_mime: mimeText
          .split(",")
          .map((value) => value.trim().toLowerCase())
          .filter(Boolean),
        filename_strategy: draft.filename_strategy,
        date_subfolder: draft.date_subfolder,
      });
      onSaved(saved);
      notify("Upload rules saved.", "success");
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : "Could not save the settings.");
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <section className="panel">
      <header className="panel__head">
        <span className="label">Upload rules</span>
      </header>

      <form className="panel__body" onSubmit={handleSubmit}>
        {error && (
          <div className="notice" role="alert">
            {error}
          </div>
        )}

        <div className="field">
          <label className="label" htmlFor="base-uri">
            Destination
          </label>
          <input
            id="base-uri"
            type="text"
            value={draft.base_uri}
            onChange={(event) => setDraft({ ...draft, base_uri: event.target.value })}
          />
          <span className="field__hint">The Cloudreve folder images are written to.</span>
        </div>

        <label className="checkbox">
          <input
            type="checkbox"
            checked={draft.date_subfolder}
            onChange={(event) => setDraft({ ...draft, date_subfolder: event.target.checked })}
          />
          <span>
            Sort into year and month folders
            <span className="field__hint" style={{ display: "block" }}>
              Adds /2026/07 under the destination.
            </span>
          </span>
        </label>

        <div className="field">
          <label className="label" htmlFor="filename-strategy">
            Filenames
          </label>
          <select
            id="filename-strategy"
            value={draft.filename_strategy}
            onChange={(event) =>
              setDraft({ ...draft, filename_strategy: event.target.value as Settings["filename_strategy"] })
            }
          >
            <option value="hash">Keep the name, add a short suffix</option>
            <option value="original">Keep the name exactly</option>
            <option value="uuid">Replace with a random id</option>
          </select>
          <span className="field__hint">
            {draft.filename_strategy === "original"
              ? "Two uploads with the same name will collide."
              : "Prevents one upload from overwriting another."}
          </span>
        </div>

        <div className="field">
          <label className="label" htmlFor="max-size">
            Size limit
          </label>
          <input
            id="max-size"
            type="text"
            value={sizeText}
            onChange={(event) => setSizeText(event.target.value)}
          />
        </div>

        <div className="field">
          <label className="label" htmlFor="allowed-mime">
            Accepted types
          </label>
          <input
            id="allowed-mime"
            type="text"
            value={mimeText}
            onChange={(event) => setMimeText(event.target.value)}
          />
          <span className="field__hint">Comma separated. Leave empty to accept anything.</span>
        </div>

        <div className="field">
          <label className="label" htmlFor="policy-id">
            Storage policy
          </label>
          <input
            id="policy-id"
            type="text"
            placeholder="Cloudreve default"
            value={draft.policy_id}
            onChange={(event) => setDraft({ ...draft, policy_id: event.target.value })}
          />
          <span className="field__hint">Leave empty to use the account's default policy.</span>
        </div>

        <button className="btn btn--primary" type="submit" disabled={isSaving}>
          {isSaving ? "Saving" : "Save rules"}
        </button>
      </form>
    </section>
  );
}
